import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';

import fetch from 'cross-fetch';
import { NextPageContext } from 'next';
import { makeUrlWithLang } from '@/utils/helpers';
import { CONFIG } from '@/constants/config';
import { logError } from '@/utils/log-error';

let apolloClientSingleton: ApolloClient<NormalizedCacheObject> | null = null;
let currentLang: string | undefined;

const isBrowser = typeof window !== 'undefined';

export function createApolloClient({
  lang,
  initialState,
  context,
}: {
  lang?: string;
  initialState?: NormalizedCacheObject | null;
  context?: NextPageContext;
}) {
  if (!CONFIG.protocol || !CONFIG.host) {
    logError(
      'createApolloClient ERROR: "CONFIG.protocol" or "CONFIG.host" is not defined'
    );
  }

  const cookies = context?.req?.headers?.cookie;

  const uri = makeUrlWithLang({
    protocol: CONFIG.protocol || '',
    host: CONFIG.host || '',
    path: '/graphql',
    lang,
  });

  const httpLink = createHttpLink({
    uri,
    credentials: 'include',
    fetch,
    headers: {
      ...(cookies && { Cookie: cookies }),
    },
  });

  const typePolicies = {
    User: {
      keyFields: ['id'] as const,
    },
    Query: {
      fields: {
        ads: {
          keyArgs: [
            'adKind',
            'filters',
            'first',
            'source',
            'orderType',
            'keywords',
            'keywordsSource',
          ],
        },
        adDetails: {
          keyArgs: ['legacyId', 'source'],
        },
      },
    },
  };

  if (!isBrowser) {
    return new ApolloClient({
      link: from([httpLink]),
      cache: new InMemoryCache({ typePolicies }).restore(initialState || {}),
      ssrMode: true,
    });
  }

  if (!apolloClientSingleton || currentLang !== lang) {
    currentLang = lang;

    apolloClientSingleton = new ApolloClient({
      link: from([httpLink]),
      cache: new InMemoryCache({ typePolicies }).restore(initialState || {}),
      ssrMode: false,
    });
  }

  return apolloClientSingleton;
}
